import { useState, useEffect, useCallback } from "react";
import { PlayCircle, ChevronLeft, ChevronRight } from "lucide-react";

/** Visionneuse navigable du tuto « partager dans 10 groupes ».
 *  6 diapos (intro + 5 étapes). Navigation : flèches latérales, boutons,
 *  points cliquables et flèches du clavier (←/→). Repliée par défaut. */
const N = 6;
const SLIDES = Array.from({ length: N }, (_, i) => `/tuto/slide-${i}.png`);

export default function FbShareTuto({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const prev = useCallback(() => setI((v) => (v - 1 + N) % N), []);
  const next = useCallback(() => setI((v) => (v + 1) % N), []);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, prev, next]);

  return (
    <div className={className} data-testid="fb-share-tuto">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="fb-share-tuto-toggle"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1877F2] hover:text-[#0f5fd0] transition-colors">
        <PlayCircle size={16} /> {open ? "Masquer le tuto" : "Voir le tuto : partager dans 10 groupes"}
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white p-2" data-testid="fb-share-tuto-media">
          <div className="relative">
            <img src={SLIDES[i]} loading="lazy" draggable={false}
              alt={i === 0 ? "Introduction du tutoriel" : `Étape ${i} du tutoriel de partage`}
              className="w-full block rounded-lg select-none" data-testid="fb-tuto-img" />
            <button type="button" onClick={prev} aria-label="Précédent" data-testid="fb-tuto-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow border border-slate-200 grid place-items-center text-[#E05638] hover:bg-white transition-colors">
              <ChevronLeft size={22} />
            </button>
            <button type="button" onClick={next} aria-label="Suivant" data-testid="fb-tuto-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow border border-slate-200 grid place-items-center text-[#E05638] hover:bg-white transition-colors">
              <ChevronRight size={22} />
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <button type="button" onClick={prev} data-testid="fb-tuto-prev-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              <ChevronLeft size={16} /> Précédent
            </button>
            <div className="flex items-center gap-1.5">
              {SLIDES.map((_, k) => (
                <button key={k} type="button" onClick={() => setI(k)} aria-label={`Aller à la diapo ${k + 1}`}
                  data-testid={`fb-tuto-dot-${k}`}
                  className={k === i
                    ? "w-6 h-2.5 rounded-full bg-[#E05638] transition-all"
                    : "w-2.5 h-2.5 rounded-full bg-slate-300 hover:bg-slate-400 transition-all"} />
              ))}
            </div>
            <button type="button" onClick={next} data-testid="fb-tuto-next-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#E05638] text-white text-sm font-semibold hover:brightness-110 transition-all">
              Suivant <ChevronRight size={16} />
            </button>
          </div>
          <div className="mt-1 text-center text-xs text-slate-400">
            {i === 0 ? "Intro" : `Étape ${i} / 5`} — flèches, points ou clavier (←/→)
          </div>
        </div>
      )}
    </div>
  );
}
